import { useEffect, useState } from "react";
import classNames from "classnames";

const API2_URL = "https://autoapi.cdn2.io";

interface UsageSummary {
  dailyUsedSeconds?: number;
  dailyLimitSeconds?: number;
  weeklyUsedSeconds?: number;
  weeklyLimitSeconds?: number;
  err?: string;
}

interface QuotaBarProps {
  refreshKey?: number;
}

function formatTime(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function Meter({
  label,
  used,
  limit,
  periodWord,
}: {
  label: string;
  used: number;
  limit: number;
  periodWord: string;
}) {
  const remaining = Math.max(0, limit - used);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;

  const fillClass = classNames(
    "h-full rounded-full transition-[width] duration-500 ease-out",
    {
      "bg-red-500": pct > 80,
      "bg-amber-400": pct > 50 && pct <= 80,
      "bg-gradient-to-r from-violet-500 to-fuchsia-500": pct <= 50,
    }
  );

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">
          {label}
        </span>
        <span className="text-xs tabular-nums text-gray-400">
          {formatTime(used)} / {formatTime(limit)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className={fillClass} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-right text-[0.7rem] text-gray-400/80">
        {formatTime(remaining)} left {periodWord}
      </div>
    </div>
  );
}

export function QuotaBar({ refreshKey }: QuotaBarProps = {}) {
  const [data, setData] = useState<UsageSummary | null>(null);

  const refresh = async () => {
    const jwt = localStorage.getItem("api2JWT");
    if (!jwt) {
      setData(null);
      return;
    }
    try {
      const res = await fetch(`${API2_URL}/api/Usage/summary`, {
        method: "GET",
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) {
        setData(null);
        return;
      }
      const json: UsageSummary = await res.json();
      if (json.err || !json.dailyLimitSeconds) {
        setData(null);
        return;
      }
      setData(json);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    refresh();
  }, [refreshKey]);

  if (!data || !data.dailyLimitSeconds) return null;

  const hasWeekly = !!data.weeklyLimitSeconds;

  return (
    <div className="glass mb-4 w-full rounded-lg px-4 py-3 text-white">
      <div className="mb-2.5 flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-widest text-gray-400">
        <svg
          className="h-3 w-3"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        </svg>
        SDXL V2 Generation Time
      </div>
      <div
        className={classNames("flex flex-col gap-3", {
          "md:flex-row md:gap-6": hasWeekly,
        })}
      >
        <div className={classNames({ "flex-1": hasWeekly })}>
          <Meter
            label="Daily"
            used={data.dailyUsedSeconds || 0}
            limit={data.dailyLimitSeconds}
            periodWord="today"
          />
        </div>
        {hasWeekly && (
          <div className="flex-1">
            <Meter
              label="Weekly"
              used={data.weeklyUsedSeconds || 0}
              limit={data.weeklyLimitSeconds!}
              periodWord="this week"
            />
          </div>
        )}
      </div>
    </div>
  );
}
